import { motion } from "framer-motion";
import {
  ArrowLeft,
  Download,
  Droplets,
  GraduationCap,
  Home,
  Mail,
  Phone,
  User,
} from "lucide-react";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import html2canvas from "html2canvas";

const DirectoryBasicDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [directoryEntry, setDirectoryEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
  
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchDirectoryDetails = async () => {
      try {
        setLoading(true);
        const data = await api.directory.getById(id);
        setDirectoryEntry(data);
      } catch (error) {
        console.error("Error fetching directory details:", error);
        setError("Failed to load directory details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDirectoryDetails();
    }
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleHome = () => {
    navigate("/home");
  };

  const handleBackToDirectory = () => {
    navigate(-1);
  };

  const formatBloodGroup = (bloodGroup) => {
    if (!bloodGroup) return "Not specified";
    return bloodGroup;
  };

  const getBloodGroupColor = (bloodGroup) => {
    if (!bloodGroup) return "bg-gray-500";

    if (bloodGroup.includes("+")) {
      return "bg-red-500";
    } else if (bloodGroup.includes("-")) {
      return "bg-blue-500";
    }
    return "bg-purple-500";
  };

  const downloadAsImage = async () => {
    if (!contentRef.current) return;
    
    setDownloading(true);
    
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: false,
        useCORS: true,
      });
      
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `${directoryEntry.name.replace(/\s+/g, '_')}_details.png`;
      link.click();
      
      showNotification("Image downloaded successfully!", "success");
    } catch (error) {
      console.error("Error downloading image:", error);
      showNotification("Failed to download image", "error");
    } finally {
      setDownloading(false);
    }
  };

  const showNotification = (message, type = "info") => {
    const existingNotification = document.querySelector(".custom-notification");
    if (existingNotification) {
      existingNotification.remove();
    }

    const notification = document.createElement("div");
    notification.className = `custom-notification alert alert-${
      type === "error" ? "danger" : type === "success" ? "success" : "info"
    }`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      min-width: 300px;
      max-width: 500px;
      padding: 15px;
      border-radius: 5px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: slideIn 0.3s ease-out;
    `;

    notification.innerHTML = `
      <div class="d-flex justify-content-between align-items-start">
        <div>
          <strong>${
            type === "error"
              ? "❌ Error"
              : type === "success"
              ? "✅ Success"
              : "ℹ️ Info"
          }</strong>
          <div class="mt-1">${message}</div>
        </div>
        <button class="btn btn-sm btn-link text-dark" onclick="this.parentElement.parentElement.remove()">
          ×
        </button>
      </div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 5000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border text-emerald-600" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-emerald-700">Loading directory details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center text-emerald-800">
          <h2 className="text-2xl font-bold mb-4">Error</h2>
          <p className="mb-4">{error}</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!directoryEntry) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center text-emerald-800">
          <h2 className="text-2xl font-bold mb-4">Directory Entry Not Found</h2>
          <p className="mb-4">
            The requested directory entry could not be found.
          </p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-4 md:p-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-emerald-900 mb-2">
              Directory Member Details
            </h1>
            <p className="text-emerald-600">
              KGC'97 Day 2026 - {directoryEntry.batch} Batch
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleBackToDirectory}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors flex items-center gap-2"
              disabled={downloading}
            >
              <ArrowLeft size={18} />
              Back
            </button>
            <button
              onClick={handleHome}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors flex items-center gap-2"
              disabled={downloading}
            >
              <Home size={18} />
              Home
            </button>
            
            {/* Single Download Button */}
            <button
              onClick={downloadAsImage}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
              disabled={downloading}
            >
              <Download size={18} />
              {downloading ? "Downloading..." : "Download Image"}
            </button>
          </div>
        </div>

        {/* Main Content - Single Column Layout */}
        <div ref={contentRef} className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Profile Card - Left Column (Full width on mobile, half on desktop) */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-2"
            >
              <div className="bg-white">
                <div className="flex flex-col md:flex-row items-start gap-8">
                  {/* Profile Image */}
                  <div className="flex flex-col items-center md:items-start md:w-1/3">
                    <div className="relative">
                      {directoryEntry.imageUrl ? (
                        <img
                          src={`${API_BASE_URL}${directoryEntry.imageUrl}`}
                          alt={directoryEntry.name}
                          className="w-48 h-48 rounded-full object-cover border-4 border-emerald-100 shadow-lg mx-auto md:mx-0"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              directoryEntry.name
                            )}&background=10b981&color=fff&size=192`;
                          }}
                        />
                      ) : (
                        <div className="w-48 h-48 rounded-full bg-emerald-100 flex items-center justify-center border-4 border-emerald-200 mx-auto md:mx-0">
                          <User className="text-emerald-600" size={80} />
                        </div>
                      )}
                    </div>

                    <div className="text-center md:text-left mt-4">
                      <h2 className="text-2xl font-bold text-emerald-900 mb-2">
                        {directoryEntry.name}
                      </h2>
                      <div className="flex items-center justify-center md:justify-start gap-2">
                        <GraduationCap size={16} className="text-emerald-600" />
                        <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full font-medium">
                          {directoryEntry.batch} Batch
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information - Right side */}
                  <div className="md:w-2/3 w-full">
                    <h3 className="text-xl font-bold text-emerald-900 mb-4 pb-2 border-b border-emerald-100">
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Phone size={18} className="text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-emerald-600">Mobile</p>
                          <p className="font-medium text-emerald-900 truncate">
                            {directoryEntry.mobile}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Mail size={18} className="text-emerald-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-emerald-600">Email</p>
                          <p className="font-medium text-emerald-900 break-all">
                            {directoryEntry.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg md:col-span-2">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Droplets size={18} className="text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm text-emerald-600">Blood Group</p>
                          <span
                            className={`px-3 py-1 rounded-full text-white font-medium inline-block ${getBloodGroupColor(
                              directoryEntry.bloodGroup
                            )}`}
                          >
                            {formatBloodGroup(directoryEntry.bloodGroup)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Footer Note inside the download area */}
          <div className="mt-8 text-center text-emerald-600 text-sm p-6 border-t border-emerald-100">
            <p className="mb-2">
              This information is part of the KGC'97 Alumni Directory.
            </p>
            <p>
              For any updates or corrections, please contact the directory
              administrator.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DirectoryBasicDetailsPage;